import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser,
  faCamera,
  faSave,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import './Profile.css';

const Profile = () => {
  const { user, setUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email
      });
      if (user.avatar && user.avatar.trim() !== '') {
        setAvatarPreview(`https://blog.bonlineco.com/storage/${user.avatar}`);
      }
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      processAvatarFile(file);
    }
  };

  const processAvatarFile = (file) => {
    if (file && file.type.startsWith('image/')) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      processAvatarFile(file);
    }
  };

  const handleAvatarUpload = async () => {
    if (!avatarFile) return;

    setUploading(true);
    setMessage('');

    try {
      const formData = new FormData();
      formData.append('avatar', avatarFile);

      const response = await apiService.uploadAvatar(formData);
      setUser(response.data.user);
      setMessage('Avatar uploaded successfully!');
      setAvatarFile(null);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error uploading avatar:', error);
      setMessage('Failed to upload avatar');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await apiService.updateProfile(formData);
      setUser(response.data.user);
      setMessage('Profile updated successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-header">
        <h2>
          <FontAwesomeIcon icon={faUser} style={{ marginRight: '12px', color: '#8b5cf6' }} />
          My Profile
        </h2>
        <p>Manage your account settings and avatar</p>
      </div>

      {message && (
        <div className={`profile-message ${message.includes('Failed') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      <div className="profile-content">
        {/* Avatar Section */}
        <div className="profile-section profile-avatar-section">
          <h3>Profile Picture</h3>
          <div className="profile-avatar-container">
            <div 
              className="profile-avatar-display"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {(avatarPreview && avatarPreview.trim() !== '') ? (
                <img 
                  src={avatarPreview} 
                  alt="Avatar" 
                  className="profile-avatar-image" 
                  onError={(e) => { 
                    e.target.style.display = 'none';
                    setAvatarPreview(null);
                  }} 
                />
              ) : (
                <div className="profile-avatar-placeholder">
                  <FontAwesomeIcon icon={faUser} />
                </div>
              )}
              <label htmlFor="avatar-upload" className="profile-avatar-upload-btn">
                <FontAwesomeIcon icon={faCamera} />
                <input
                  type="file"
                  id="avatar-upload"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  style={{ display: 'none' }}
                />
              </label>
              <div className={`profile-avatar-drag-overlay ${isDragging ? 'active' : ''}`}>
                <FontAwesomeIcon icon={faCamera} />
                Drop image here
              </div>
            </div>
            {avatarFile && (
              <div className="profile-avatar-actions">
                <button
                  onClick={handleAvatarUpload}
                  className="profile-btn profile-btn-primary profile-btn-upload"
                  disabled={uploading}
                >
                  <FontAwesomeIcon icon={uploading ? faSpinner : faCamera} spin={uploading} />
                  {uploading ? 'Uploading...' : 'Upload Avatar'}
                </button>
                <button
                  onClick={() => {
                    setAvatarFile(null);
                    setAvatarPreview((user?.avatar && user.avatar.trim() !== '') ? `https://blog.bonlineco.com/storage/${user.avatar}` : null);
                  }}
                  className="profile-btn profile-btn-cancel"
                  disabled={uploading}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          <div className="profile-upload-instructions">
            <p className="profile-hint">
              <strong>💡 Pro tip:</strong> Drag & drop your image or click the camera button
            </p>
            <p className="profile-hint-secondary">
              Square image recommended • Min 200x200px • Max 2MB • JPG, PNG, GIF
            </p>
          </div>
        </div>

        {/* Profile Form */}
        <form onSubmit={handleSubmit} className="profile-section">
          <h3>Account Information</h3>
          
          <div className="profile-field">
            <label htmlFor="name">Full Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="profile-field">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="profile-actions">
            <button type="submit" className="profile-btn profile-btn-primary" disabled={loading}>
              <FontAwesomeIcon icon={faSave} />
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Profile;
